import { inject, Injectable, Injector } from '@angular/core'

import { pluralize } from '@app/core/pluralize'
import { FrameService } from '@app/modules/frame'
import { PopupService } from '@app/modules/popup/popup.service'
import { UploadStatusComponent } from '@app/modules/s3-upload/components/upload-status/upload-status.component'
import { Timespan } from '@app/modules/time/core/timespan.enum'
import { TooltipService } from '@app/modules/ui/components/tooltip/tooltip.service'
import { ExplainerService } from '@app/overlays/explainer/explainer.service'
import { ModalService } from '@app/overlays/modal/modal.service'
import { MemberService } from '@app/services/member.service'
import { SiteStore } from '@app/store/site.store'

import { ConversationRemoveConfirmModalComponent } from '../components/conversation-remove-confirm-modal/conversation-remove-confirm-modal.component'
import { GreetingExplainerModalComponent } from '../components/greeting-explainer-modal/greeting-explainer-modal.component'
import type { ConversationProfile } from '../models/conversation-profile.model'
import type { Conversation } from '../models/conversation.model'
import type { MessageForm } from '../models/message-form.model'
import type { Message } from '../models/message.model'
import { BoostMessageInsufficientCreditsModalComponent } from '../modules/boost/components/boost-message-insufficient-credits-modal/boost-message-insufficient-credits-modal.component'
import { BoostMessageModalComponent } from '../modules/boost/components/boost-message-modal/boost-message-modal.component'
import { MessagePhotoAlbumDeleteConfirmModalComponent } from '../modules/message-photo/components/message-photo-album-delete-confirm-modal/message-photo-album-delete-confirm-modal.component'
import { MessagePhotoAlbumDisapprovedModalComponent } from '../modules/message-photo/components/message-photo-album-disapproved-modal/message-photo-album-disapproved-modal.component'
import { MessagePhotoAlbumInsufficientCreditsModalComponent } from '../modules/message-photo/components/message-photo-album-insufficient-credits-modal/message-photo-album-insufficient-credits-modal.component'
import { MessagePhotoAlbumModalComponent } from '../modules/message-photo/components/message-photo-album-modal/message-photo-album-modal.component'
import type { MessagePhotoAlbumConfig } from '../modules/message-photo/components/message-photo-album-modal/message-photo-album.store'
import { MESSAGE_PHOTO_ALBUM_CONFIG } from '../modules/message-photo/components/message-photo-album-modal/message-photo-album.store'
import { MessagePhotoAlbumRulesModalComponent } from '../modules/message-photo/components/message-photo-album-rules-modal/message-photo-album-rules-modal.component'
import { MessagePhotoAlbumSingleDisapprovedModalComponent } from '../modules/message-photo/components/message-photo-album-single-disapproved-modal/message-photo-album-single-disapproved-modal.component'
import { MessagePhotoGalleryComponent } from '../modules/message-photo/components/message-photo-gallery/message-photo-gallery.component'
import { MessagePhotoPrivacyDisclosureComponent } from '../modules/message-photo/components/message-photo-privacy-disclosure/message-photo-privacy-disclosure.component'
import { MessagePhotoUnlockInsufficientCreditsModalComponent } from '../modules/message-photo/components/message-photo-unlock-insufficient-credits-modal/message-photo-unlock-insufficient-credits-modal.component'
import { ReadReceiptsModalComponent } from '../modules/read-receipts/components/read-receipts-modal/read-receipts-modal.component'
import { VoiceMessageBoostInsufficientCreditsModalComponent } from '../modules/voice-message/components/voice-message-boost-insufficient-credits-modal/voice-message-boost-insufficient-credits-modal.component'
import { VoiceMessageCreatorModalComponent } from '../modules/voice-message/components/voice-message-creator-modal/voice-message-creator-modal.component'
import type { VoiceMessageRecorderConfig } from '../modules/voice-message/components/voice-message-recorder/voice-message-recorder.store'
import { VOICE_MESSAGE_RECORDER_CONFIG } from '../modules/voice-message/components/voice-message-recorder/voice-message-recorder.store'

@Injectable({ providedIn: 'root' })
export class ConversationUIService {
  private readonly memberService = inject(MemberService)
  private readonly modalService = inject(ModalService)
  private readonly frameService = inject(FrameService)
  private readonly siteStore = inject(SiteStore)
  private readonly popupService = inject(PopupService)
  private readonly explainerService = inject(ExplainerService)
  private readonly tooltipService = inject(TooltipService)

  openBoostMessageInsufficientCreditsModal(
    profile: ConversationProfile,
  ): BoostMessageInsufficientCreditsModalComponent {
    const { instance } = this.modalService.create(BoostMessageInsufficientCreditsModalComponent)
    instance.profile = profile
    return instance
  }

  openNewMessageBoostModal(conversation: Conversation): void {
    const { instance, componentRef } = this.modalService.create(BoostMessageModalComponent)
    instance.profile = conversation.profile
    instance.headlineCopy = 'Introducing Message Boosts'
    instance.buttonVariant = 'fill-green'
    instance.buttonCopy = 'Got It'
    this.popupService.onPopupOpen('message-boost-new-intro', { componentRef })
  }

  openExistingMessageBoostModal(conversation: Conversation): BoostMessageModalComponent {
    const { instance, componentRef } = this.modalService.create(BoostMessageModalComponent)
    instance.profile = conversation.profile
    instance.headlineCopy = 'Boost Your Sent Message'
    instance.buttonVariant = 'fill-gold'
    instance.buttonCopy = 'Boost'
    this.popupService.onPopupOpen('message-boost-existing-intro', {
      componentRef,
    })

    return instance
  }

  openMakeSureTheySeeItModal(conversation: Conversation): BoostMessageModalComponent {
    const { instance, componentRef } = this.modalService.create(BoostMessageModalComponent)
    instance.profile = conversation.profile
    instance.headlineCopy = 'Make Sure They See It'
    instance.buttonVariant = 'fill-gold'
    instance.buttonCopy = 'Boost'

    this.popupService.onPopupOpen('message-boost-encouragement', { componentRef })

    return instance
  }

  openUnlockReadReceiptsIntroModal(): ReadReceiptsModalComponent {
    const { instance, componentRef } = this.modalService.create(ReadReceiptsModalComponent)
    instance.headlineCopy = 'Unlock Read Receipts'
    instance.bodyCopy = `See when your messages have been read with read receipts, just ${this.siteStore.SA.Constants.costs_read_receipts} credits per conversation.`
    this.popupService.onPopupOpen('message-read-receipts-intro', {
      componentRef,
    })

    return instance
  }

  openKnowWhenTheyReadItModal(): ReadReceiptsModalComponent {
    const { instance, componentRef } = this.modalService.create(ReadReceiptsModalComponent)
    instance.headlineCopy = 'Know When They Read It'
    instance.bodyCopy = `For just ${this.siteStore.SA.Constants.costs_read_receipts} credits per conversation, know when your messages have been read with read receipts.`
    this.popupService.onPopupOpen('message-read-receipts-encouragement', {
      componentRef,
    })

    return instance
  }

  openMessagePhotoUnlockInsufficientCreditsModal(): MessagePhotoUnlockInsufficientCreditsModalComponent {
    return this.modalService.create(MessagePhotoUnlockInsufficientCreditsModalComponent).instance
  }

  openMessagePhotoGallery(message: Message & { type: 'photo'; status: 'viewable' }, injector: Injector): void {
    if (this.popupService.getPopupRequest('message-photos-privacy') && !message.is_me) {
      const instance = this.openMessagePhotoPrivacyDisclosure()
      instance.onClose(() => {
        this.openMessagePhotoGallery(message, injector)
      })
      return
    }
    const { viewRef } = this.frameService.attachView(MessagePhotoGalleryComponent, {
      ...this.frameService.CONFIG_FACTORIES.FIXED_FULL_SCREEN(),
      disposeOnNavigation: true,
      backdropCloseOnClick: true,
      injector,
    })
    viewRef.instance.message = message
  }

  openMessagePhotoPrivacyDisclosure(): MessagePhotoPrivacyDisclosureComponent {
    const { viewRef } = this.frameService.attachView(MessagePhotoPrivacyDisclosureComponent, {
      ...this.frameService.CONFIG_FACTORIES.FIXED_CENTER(),
      backdropStyle: 'black',
      backdropCloseOnClick: false,
    })
    this.popupService.onPopupOpen('message-photos-privacy', { componentRef: viewRef })
    return viewRef.instance
  }

  openConversationRemoveConfirmModal(): ConversationRemoveConfirmModalComponent {
    return this.modalService.create(ConversationRemoveConfirmModalComponent).instance
  }

  openGreetingExplainerModal(): void {
    this.modalService.create(GreetingExplainerModalComponent, { showCloseButton: false, closeOnBackdropClick: true })
  }

  openMessagePhotoAlbumRulesModal(conversation: Conversation): MessagePhotoAlbumRulesModalComponent {
    const { instance, componentRef } = this.modalService.create(MessagePhotoAlbumRulesModalComponent)

    instance.cost = conversation.show_message_photo_cost
      ? this.memberService.account.costs.send_message_photo
      : undefined

    this.popupService.onPopupOpen('message-photos-rules', { componentRef })

    return instance
  }

  openMessagePhotoAlbumModal(
    config: MessagePhotoAlbumConfig,
    parentInjector: Injector,
  ): MessagePhotoAlbumModalComponent {
    return this.modalService.create(MessagePhotoAlbumModalComponent, {
      injector: Injector.create({
        providers: [{ provide: MESSAGE_PHOTO_ALBUM_CONFIG, useValue: config }],
        parent: parentInjector,
      }),
      desktopWidth: '532px',
      padded: false,
      scrollable: false,
      fillHeight: true,
    }).instance
  }

  openDisapprovedPhotoModal(count: number, conversation: Conversation, injector: Injector): void {
    if (this.memberService.account.benefactor) {
      this.modalService.create(MessagePhotoAlbumSingleDisapprovedModalComponent)
      return
    }
    const { instance } = this.modalService.create(MessagePhotoAlbumDisapprovedModalComponent)
    instance.count = count
    instance.username = conversation.profile.username
    instance.onConfirm(() => {
      this.openMessagePhotoAlbumModal({ initialSection: 'approved', conversation }, injector)
    })
  }

  openMessagePhotoAlbumDeleteConfirmModal(): MessagePhotoAlbumDeleteConfirmModalComponent {
    return this.modalService.create(MessagePhotoAlbumDeleteConfirmModalComponent).instance
  }

  openUploadStatusComponent(): UploadStatusComponent {
    return this.modalService.create(UploadStatusComponent, {
      showCloseButton: false,
      closeOnBackdropClick: false,
    }).instance
  }

  openMessagePhotoAlbumInsufficientCreditsModal(): MessagePhotoAlbumInsufficientCreditsModalComponent {
    return this.modalService.create(MessagePhotoAlbumInsufficientCreditsModalComponent).instance
  }

  openMessagePhotoAttachExplainer(element: HTMLElement): void {
    this.popupService.askForShowing('message-photos-send-intro', () => {
      if (this.memberService.account.benefactor) {
        const { componentRef } = this.explainerService.show(
          element,
          50,
          'Send Private Photos',
          `You can now send private photos directly to members for just ${this.memberService.account.costs.send_message_photo} credits each.`,
        )
        return { componentRef }
      } else {
        const time = pluralize(this.memberService.account.photos_expiration_hours, 'hour')
        const { componentRef } = this.tooltipService.show({
          anchor: element,
          title: `Send photos directly to this member, photos disappear after ${time}`,
          hideTrigger: 'outside_click',
          backdropClickThrough: false,
        })
        return { componentRef }
      }
    })
  }

  openMessagePhotoUnlockExplainer(element: HTMLElement): void {
    this.popupService.askForShowing('message-photos-unlock-intro', () => {
      const { componentRef } = this.explainerService.show(
        element,
        24,
        'Unlock Photos',
        `Photos sent to you can be unlocked for just ${this.memberService.account.costs.unlock_message_photo} credits. You have ${this.memberService.account.photos_expiration_hours} hours to unlock before it expires.`,
      )
      return { componentRef }
    })
  }

  openAnchorMessageHighlighter(element: HTMLElement): void {
    this.popupService.askForShowing('message-highlight', () => {
      element.scrollIntoView({ behavior: 'instant', block: 'center', inline: 'center' })
      return {
        fake: true,
      }
    })
  }

  openExpiredPhotoModal(element: HTMLElement): void {
    this.popupService.askForShowing('message-photo-expired', () => {
      const { componentRef } = this.explainerService.show(
        element,
        30,
        'Photo Expired',
        `Photos last ${this.memberService.account.photos_expiration_hours} hours from the time they're sent. You can always ask them to resend it.`,
      )
      return { componentRef }
    })
  }

  openVoiceMessageBoostInsufficientCreditsModalComponent(): VoiceMessageBoostInsufficientCreditsModalComponent {
    return this.modalService.create(VoiceMessageBoostInsufficientCreditsModalComponent).instance
  }

  openVoiceMessageCreatorModal(
    conversation: Conversation,
    parentInjector: Injector,
    messageForm: MessageForm,
  ): VoiceMessageCreatorModalComponent {
    const minDurationMs = this.memberService.account.voice_messages_min_duration_seconds * Timespan.Second
    const maxDurationMs = this.memberService.account.voice_messages_max_duration_seconds * Timespan.Second

    const config: VoiceMessageRecorderConfig = {
      conversation,
      messageForm,
      minDurationMs,
      maxDurationMs,
    }

    return this.modalService.create(VoiceMessageCreatorModalComponent, {
      injector: Injector.create({
        providers: [{ provide: VOICE_MESSAGE_RECORDER_CONFIG, useValue: config }],
        parent: parentInjector,
      }),
      desktopWidth: '532px',
    }).instance
  }

  openExpiredVoiceMessageModal(element: HTMLElement): void {
    this.popupService.askForShowing('voice-message-expired', () => {
      const timeExpiration = pluralize(this.memberService.account.voice_messages_expiration_hours, 'hour')
      const timeExpirationAfterPlay = pluralize(
        this.memberService.account.voice_messages_unlock_expiration_minutes,
        'minute',
      )

      return this.explainerService.show(
        element,
        30,
        'Voice Message Expired',
        `Voice messages last ${timeExpiration} from the time they’re sent, and expire ${timeExpirationAfterPlay} after being played.`,
      )
    })
  }

  openVoiceMessageExplainer(element: HTMLElement): void {
    this.popupService.askForShowing('voice-message-intro', () => {
      if (this.memberService.account.benefactor) {
        const cost = pluralize(this.memberService.account.costs.send_voice_message, 'credit')
        return this.explainerService.show(
          element,
          50,
          'Send Voice Messages',
          `You can now send voice messages directly to members for just ${cost} each.`,
        )
      } else {
        const time = pluralize(this.memberService.account.voice_messages_expiration_hours, 'hour')
        return this.explainerService.show(
          element,
          50,
          'Send Voice Messages',
          `You can now send voice messages directly to this member. Voice messages disappear after ${time}.`,
        )
      }
    })
  }

  openVoiceMessageUnlockExplainer(element: HTMLElement): void {
    this.popupService.askForShowing('voice-message-unlock-intro', () => {
      const cost = pluralize(this.memberService.account.costs.unlock_voice_message, 'credit')
      const time = pluralize(this.memberService.account.voice_messages_expiration_hours, 'hour')
      return this.explainerService.show(
        element,
        24,
        'Unlock Voice Messages',
        `Voice messages sent to you can be unlocked for just ${cost}. You have ${time} to unlock before it expires.`,
      )
    })
  }

  openVoiceMessagePlayExplainer(element: HTMLElement): void {
    this.popupService.askForShowing('voice-message-play-intro', () => {
      if (this.memberService.account.benefactor) {
        const time = pluralize(this.memberService.account.voice_messages_unlock_expiration_minutes, 'minute')
        return this.explainerService.show(
          element,
          50,
          'Play Voice Messages',
          `Just press play to start listening. Voice messages expire ${time} after you unlock them.`,
        )
      } else {
        const timeExpirationAfterReceive = pluralize(this.memberService.account.voice_messages_expiration_hours, 'hour')
        const timeExpirationAfterPlay = pluralize(
          this.memberService.account.voice_messages_unlock_expiration_minutes,
          'minute',
        )
        return this.explainerService.show(
          element,
          50,
          'Play Voice Messages',
          `Just press play to start listening. Voice messages expire ${timeExpirationAfterReceive} after you receive them, and ${timeExpirationAfterPlay} after you play them.`,
        )
      }
    })
  }
}
